import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from '@fullstory/react-hooks';
import {
  AppDispatch,
  fullstorySelectors,
  PagesRequestActions,
  pagesSelectors,
  getPagePropsLoading,
  SearchMetaAsyncRequestActions,
  SearchMetaRequestActions,
} from '@fullstory/redux';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { usePageDefActions } from '../hooks/usePageDefActions';
import { getPagePropertiesByPage } from '@fullstory/events-ui';
import { buildHostnameReq, buildPathReq } from '../utils/utils';
import { AutocompleteRequest } from '@fullstory/proto/fs/services/paige';

export const usePageDefDependencies = (pageId: number) => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const flags = useFlags();
  const dispatch = useDispatch<AppDispatch>();

  const restricted = !flags.hasBit(Flag.PermModifyPageDefinitions);
  // might be setting data into hostname when it doesnt exist anymore
  // TODO: clean up useEffect so setHostnames isn't called if this pageDefModalContainer is unmounted
  const [hostnames, setHostnames] = useState<string[]>([]);
  useEffect(() => {
    let isMounted = true;
    // TODO handle failed request
    dispatch(PagesRequestActions.autocomplete(buildHostnameReq(orgId)))
      .then(rsp => {
        // ensure that '*' is always the first option in the list
        if (isMounted) {
          setHostnames(['*'].concat(rsp.suggestions.filter(s => s !== '*')));
        }
      })
      .catch(err => {
        console.error(err);
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch, setHostnames, orgId, flags]);

  const getSuggestions = useCallback(
    async (path: string, fragment: string, domain: string, scope: AutocompleteRequest.Scope) => {
      try {
        // TODO handle failed request
        const rsp = await dispatch(
          PagesRequestActions.autocomplete(buildPathReq(orgId, path, fragment, domain, scope)),
        );
        return rsp.suggestions;
      } catch (err) {
        console.error(err);
      }
      return [];
    },
    [dispatch, orgId],
  );

  const { pagesById } = pagesSelectors;
  const pages = useSelector(pagesById);

  const getPageById = useCallback(
    (pageDefId: number | undefined): PageDefinition | undefined =>
      pageDefId ? pages.get(pageDefId) : undefined,
    [pages],
  );

  const {
    onSave,
    onArchive,
    onRestore,
    rejectSuggestion: onRejectSuggestion,
  } = usePageDefActions();

  const canArchiveProperties = flags.hasBit(Flag.PermModifyCustomVars);
  const pagePropsLoading = useSelector(getPagePropsLoading);
  const propertiesByPage = useSelector(getPagePropertiesByPage);
  const presentPageConnections = !!pageId;

  useEffect(() => {
    dispatch(SearchMetaRequestActions.getHiddenVars(orgId));
  }, [orgId, dispatch]);

  const getConnections = useCallback(() => {
    if (presentPageConnections) {
      dispatch(
        SearchMetaAsyncRequestActions.getCustomSchemaConnections(orgId, { pages: [pageId] }),
      );
    }
  }, [pageId, presentPageConnections, orgId, dispatch]);

  useEffect(() => {
    getConnections();
  }, [getConnections]);

  const { propertiesLoading, propertiesFailed, properties } = useMemo(() => {
    const properties = propertiesByPage.get(pageId);
    const propertiesLoading = pagePropsLoading.has(pageId);
    const propertiesFailed = !properties && !propertiesLoading;
    return {
      propertiesLoading,
      propertiesFailed,
      properties: properties ?? [],
    };
  }, [pageId, pagePropsLoading, propertiesByPage]);

  return {
    orgId,
    restricted,
    hostnames,
    getSuggestions,
    getPageById,
    onSave,
    onArchive,
    onRestore,
    onRejectSuggestion,
    canArchiveProperties,
    propertiesLoading,
    propertiesFailed,
    properties,
    getConnections,
    presentPageConnections,
  };
};
